import {
  ProcessType,
  CellPoseModel, CellPoseModelLabels, CellPoseChannelMode,
  CellPoseSamModel, CellPoseSamModelLabels,
  YoloModels,
  RetinalLayerModels,
  StainAdapterModel, StainAdapterMode, StainAdapterChannelMode,
  StainAdapterNormalizeMethod, StainAdapterNormalizeTarget,
} from './request';
import { OperationCategory, ParamDescriptor } from './processing-pipeline/operation.model';

export type ToolGroupId =
  | 'segmentation-classifier'
  | 'color-processing'
  | 'image-manipulation'
  | 'utilities';

export interface ToolGroupDefinition {
  id: ToolGroupId;
  label: string;
  icon: string;
  sortOrder: number;
}

export interface RequestBuildContext {
  getRegions: () => any[];
}

export interface JitToolDefinition {
  operationId: string;
  processType: ProcessType;
  displayName: string;
  description: string;
  icon: string;
  groupId: ToolGroupId;

  /** Wire ProcessType for processService.run() when it differs from processType (e.g. SIFT vs ALIGNMENT). */
  serverProcessType?: ProcessType;

  /** Pipeline category. Absent means the tool is not available in the pipeline. */
  pipelineCategory?: OperationCategory;
  javaInstructionTypeName?: string;
  pipelineParams?: ParamDescriptor[];
  buildRequestBody?: (params: Record<string, any>, context: RequestBuildContext) => Record<string, any>;

  /** Called when a pipeline param changes. Returns updated params if other params should change
  (e.g. model presets). */
  onParamChange?: (changedParamId: string, params: Record<string, any>) => Record<string, any> | null;

  /** Returns filtered options for a select param based on current param values. Null = use static options. */
  filterParamOptions?: (paramId: string, currentParams: Record<string, any>)
  => { label: string; value: any }[] | null;

  /** Returns the set of visible param IDs given current values. Null = all params visible. */
  getVisibleParams?: (currentParams: Record<string, any>) => Set<string> | null;

  /** Hide this tool from the standalone process dropdown. Used for tools that
   *  exist only to back the pipeline-side registry (e.g. cellpose-sam, which
   *  is reached through the unified Cellpose process page's model dropdown). */
  hiddenFromProcessDropdown?: boolean;

  /** Engine that should provide local (in-browser) preview for this tool's
   *  pipeline step. When set, the pipeline executor delegates `execute()` to
   *  the named engine instead of passing-through (the default for server-engine
   *  steps). Used for `cellpose-sam`, which appears in the picker as a JIT
   *  server tool but has a real WebGPU preview via the `cellpose` engine. */
  previewEngineId?: string;

  /** When true, the pipeline dialog shows an explicit "Run" button in the param
   *  panel and does NOT auto-rerun on param changes. Use for heavy operations
   *  (model inference, server jobs) where every keystroke would trigger a
   *  multi-second re-execution. Maps to {@link OperationDescriptor.requiresManualRun}
   *  in the picker entry built by `toOperationDescriptor`. */
  requiresManualRun?: boolean;
}

export interface StainAbsorption {
  name: string;
  value: [number, number, number];
}

export const STAIN_ABSORPTIONS: ReadonlyArray<StainAbsorption> = [
  { name: 'hematoxylin',         value: [0.563, 0.720, 0.406] },
  { name: 'eosin',               value: [0.216, 0.801, 0.558] },
  { name: 'DAB',                 value: [0.268, 0.570, 0.776] },
  { name: 'fast red',            value: [0.214, 0.851, 0.478] },
  { name: 'fast blue',           value: [0.749, 0.606, 0.267] },
  { name: 'methyl blue',         value: [0.799, 0.591, 0.105] },
  { name: 'methyl green',        value: [0.980, 0.144, 0.133] },
  { name: 'AEC',                 value: [0.274, 0.679, 0.680] },
  { name: 'aniline blue',        value: [0.853, 0.509, 0.113] },
  { name: 'azocarmine',          value: [0.071, 0.977, 0.198] },
  { name: 'alcian blue',         value: [0.875, 0.458, 0.158] },
  { name: 'PAS',                 value: [0.175, 0.972, 0.155] },
  { name: 'hematoxylin and PAS', value: [0.553, 0.754, 0.354] },
  { name: 'Feulgen',             value: [0.464, 0.830, 0.308] },
  { name: 'methylene blue',      value: [0.553, 0.754, 0.354] },
  { name: 'orange-G',            value: [0.107, 0.368, 0.923] },
  { name: 'ponceau-fuchsin',     value: [0.100, 0.737, 0.668] },
];

export const TOOL_GROUPS: ReadonlyArray<ToolGroupDefinition> = [
  { id: 'segmentation-classifier', label: 'Segmentation/Classifier', icon: 'assets/icons/data-classification.svg', sortOrder: 1 },
  { id: 'color-processing',        label: 'Color Processing',        icon: 'pi-palette',                           sortOrder: 2 },
  { id: 'image-manipulation',      label: 'Image Manipulation',      icon: 'pi-images',                            sortOrder: 3 },
  { id: 'utilities',               label: 'Utilities',               icon: 'pi-wrench',                            sortOrder: 4 },
];

export const JIT_TOOLS: ReadonlyArray<JitToolDefinition> = [

  // ── Segmentation / Classifier ───────────────────────────────────────────────

  {
    operationId: 'htquant',
    processType: ProcessType.HTQUANT,
    displayName: 'Fibers and Nuclei',
    description: 'Quantify Fibers and Nuclei in regions of interest',
    icon: 'pi-cog',
    groupId: 'segmentation-classifier',
    pipelineCategory: 'measurement',
    javaInstructionTypeName: 'org.jax.cimg.api.dao.instr.QuantInstruction',
    pipelineParams: [
      { id: 'useRegions', label: 'Use diagram regions', type: 'boolean', defaultValue: true,
        tooltip: 'When enabled, quantification is restricted to the regions drawn on the image viewer.' },
    ],
    buildRequestBody: (_params, ctx) => ({
      regions: ctx.getRegions(),
    }),
  },
  {
    operationId: 'kidney-classifier',
    processType: ProcessType.KIDNEY,
    displayName: 'Kidney Classifier',
    description: 'Analyzes kidney histology, generating heatmaps for damage assessment, and provides detailed CSV reports at pixel, slide, and section levels, along with tissue masks',
    icon: 'pi-microchip-ai',
    groupId: 'segmentation-classifier',
    pipelineCategory: 'detection',
    javaInstructionTypeName: 'org.jax.cimg.api.dao.instr.ClassifierInstruction',
    pipelineParams: [
      { id: 'lower_bound', label: 'Lower Bound', type: 'number', defaultValue: 0.3, min: 0.0, max: 1.0, step: 0.05,
        tooltip: 'Lower confidence threshold for classification.' },
      { id: 'upper_bound', label: 'Upper Bound', type: 'number', defaultValue: 0.7, min: 0.0, max: 1.0, step: 0.05,
        tooltip: 'Upper confidence threshold for classification.' },
    ],
    buildRequestBody: (params, _ctx) => ({
      lower_bound: params.lower_bound || 0.3,
      upper_bound: params.upper_bound || 0.7,
    }),
  },
  {
    operationId: 'crop',
    processType: ProcessType.CROP,
    displayName: 'Slide Cropping',
    description: 'Threshold-based segmentation of either manually or automatically selected regions of interest.',
    icon: 'pi-cog',
    groupId: 'segmentation-classifier',
    pipelineCategory: 'transform',
    javaInstructionTypeName: 'org.jax.cimg.api.dao.instr.CropInstruction',
    pipelineParams: [
      { id: 'cropResolution', label: 'Resolution (0=full)', type: 'number', defaultValue: 0, min: 0, max: 10, step: 1,
        tooltip: 'Downsample resolution level. 0 = full resolution, higher = more downsampled.' },
    ],
    buildRequestBody: (params, ctx) => {
      const regions = ctx.getRegions();
      return {
        cropResolution: params.cropResolution || 0,
        regionSettings: {
          manualRegionResolution: 0,
          findRegions: regions.length === 0,
          cropFoundRegionsAsRectangles: true,
          manualRegions: regions,
        },
      };
    },
  },
  {
    operationId: 'retinal-layer',
    processType: ProcessType.RETINAL_LAYER,
    displayName: 'Retinal Layer Segmentation',
    description: 'Segments the retinal layers in H&E-stained eyeball images using the ResUNet or VNet models.',
    icon: 'pi-microchip-ai',
    groupId: 'segmentation-classifier',
    pipelineCategory: 'segmentation',
    javaInstructionTypeName: 'org.jax.cimg.api.dao.instr.RetinalLayerInstruction',
    // Browser-side preview runs on WebGPU via jax-ai-js; Run-on-Server is
    // unaffected and still submits a RetinalLayerInstruction. VNet and the 2025
    // ResUNet-a 20x/40x pair run client-side; the superseded
    // `resunet-a-2d-RL-40x-old` falls back to the server — see
    // RetinalLayerEngine.
    previewEngineId: 'retinal-layer',
    // This checkpoint is ~590 MB and inference is seconds per patch. Auto-rerun
    // on every keystroke would start a download the moment the operation is
    // opened, before the user has chosen anything.
    requiresManualRun: true,
    pipelineParams: [
      // VNet is the default for BOTH the browser preview and Run-on-Server, and
      // that is deliberate rather than a browser concession. Measured against
      // the masks shipped alongside these checkpoints, VNet scores mIoU 0.906
      // and the ResUNet-a variants 0.30-0.40 — and the disagreement is
      // structural, not a label permutation, since the best of all 24
      // permutations does not improve on the identity ordering. Whether those
      // masks belong to a different model generation is an open question for
      // whoever trained them. Note those figures are train-set, not held-out:
      // the bucket ships no validation split. Do not switch this default
      // without new evidence.
      //
      // Every ResUNet-a option stays selectable, and since jit-ui#124 the 2025
      // 20x/40x pair also previews in the browser. Their exports are
      // argmax-identical to the Keras checkpoints the server runs, so a browser
      // preview matches a server run of the same weights — the accuracy gap
      // above is a property of the checkpoint, not of where it executes.
      {
        id: 'model', label: 'Model Type', type: 'select', defaultValue: 'vnet_2d',
        tooltip: 'Select the type of model to run the prediction. VNet is the default: it scores ' +
          'mIoU 0.906 against the masks shipped with these checkpoints, versus 0.30-0.40 for the ' +
          'ResUNet-a variants.',
        options: [
          { label: 'ResUNet A 2D', value: 'resunet_a_2d' },
          { label: 'VNet 2D', value: 'vnet_2d' },
        ],
      },
      {
        id: 'predModel', label: 'Model Name', type: 'select',
        defaultValue: RetinalLayerModels.VNET_2D_RL_40X,
        tooltip: 'Select the fine-tuned model file. VNet 2D and the ResUNet A 20x/40x pair run ' +
          'in the browser preview; "40x (old)" is superseded and runs on the server only.',
        options: [
          { label: 'ResUNet A 2D RL 40x', value: RetinalLayerModels.RESUNET_A_2D_RL_40X },
          { label: 'ResUNet A 2D RL 20x', value: RetinalLayerModels.RESUNET_A_2D_RL_20X },
          { label: 'ResUNet A 2D RL 40x (old)', value: RetinalLayerModels.RESUNET_A_2D_RL_40X_OLD },
          { label: 'VNet 2D RL 40x', value: RetinalLayerModels.VNET_2D_RL_40X },
        ],
      },
      {
        id: 'step', label: 'Step Size', type: 'select', defaultValue: 512,
        tooltip: 'Step size for overlap between patches: 512 = no overlap, 256 = 256px overlap, 128 = 384px overlap.',
        options: [
          { label: '512', value: 512 },
          { label: '256', value: 256 },
          { label: '128', value: 128 },
        ],
      },
      {
        id: 'patchSize', label: 'Patch Size', type: 'select', defaultValue: 512,
        tooltip: 'Image size used to train the model.',
        options: [
          { label: '512', value: 512 },
          { label: '256', value: 256 },
        ],
      },
      // ── Browser-only, below. The server derives or ignores these. ──────────
      {
        id: 'downsamplingFactor', label: 'Downsampling Factor', type: 'number',
        defaultValue: 1, min: 1, max: 10, step: 1,
        tooltip:
          'Browser preview only. Scale to run at, as a divisor of full resolution. ' +
          '1 = full resolution, which is what these checkpoints were trained on.',
      },
      {
        id: 'classThreshold', label: 'Class Threshold', type: 'number',
        defaultValue: 0.5, min: 0.05, max: 0.95, step: 0.05,
        tooltip:
          'Browser preview only. Minimum probability for a pixel to be assigned a layer; ' +
          'below it the pixel is left unlabelled rather than forced into the best class. ' +
          'Matches the server worker, which thresholds each class at 0.5 rather than taking an argmax.',
      },
      {
        id: 'minArea', label: 'Min Region Area (px²)', type: 'number',
        defaultValue: 0, min: 0, max: 100000, step: 10,
        tooltip: 'Browser preview only. Drop traced regions smaller than this.',
      },
      {
        id: 'simplifyTolerance', label: 'Outline Simplification (px)', type: 'number',
        defaultValue: 1, min: 0, max: 10, step: 0.5,
        tooltip:
          'Browser preview only. Douglas-Peucker tolerance for traced outlines; ' +
          '0 keeps every pixel-step vertex.',
      },
    ],
    buildRequestBody: (params, _ctx) => ({
      model: params.model || 'vnet_2d',
      predModel: '/app/models/' + (params.predModel || RetinalLayerModels.VNET_2D_RL_40X),
      patchSize: params.patchSize || 512,
      step: params.step || 512,
    }),
    onParamChange: (changedParamId, params) => {
      if (changedParamId === 'model') {
        const isResunet = params.model === 'resunet_a_2d';
        return {
          ...params,
          predModel: isResunet
            ? RetinalLayerModels.RESUNET_A_2D_RL_40X
            : RetinalLayerModels.VNET_2D_RL_40X,
          step: 512,
          patchSize: 512,
        };
      }
      if (changedParamId === 'predModel') {
        const is20x = params.predModel === RetinalLayerModels.RESUNET_A_2D_RL_20X;
        return {
          ...params,
          step: is20x ? 256 : 512,
          patchSize: is20x ? 256 : 512,
        };
      }
      return null;
    },
    filterParamOptions: (paramId, currentParams) => {
      if (paramId !== 'predModel') return null;
      if (currentParams.model === 'vnet_2d') {
        return [
          { label: 'VNet 2D RL 40x', value: RetinalLayerModels.VNET_2D_RL_40X },
        ];
      }
      return [
        { label: 'ResUNet A 2D RL 40x', value: RetinalLayerModels.RESUNET_A_2D_RL_40X },
        { label: 'ResUNet A 2D RL 20x', value: RetinalLayerModels.RESUNET_A_2D_RL_20X },
        { label: 'ResUNet A 2D RL 40x (old)', value: RetinalLayerModels.RESUNET_A_2D_RL_40X_OLD },
      ];
    },
  },
  {
    operationId: 'yolosegdetect',
    processType: ProcessType.YOLOSEGDETECT,
    displayName: 'YOLO',
    description: 'Detects/classifies/segments objects in images using the YOLOv8x model fine-tuned on the optic nerve area and the retina area of the H&E-stained mouse eyeball sections.',
    icon: 'pi-microchip-ai',
    groupId: 'segmentation-classifier',
    pipelineCategory: 'detection',
    javaInstructionTypeName: 'org.jax.cimg.api.dao.instr.YoloSegdetectInstruction',
    // Browser-side preview runs on WebGPU via yolo-segdetect-js; Run-on-Server is
    // unaffected and still submits a YoloSegdetectInstruction.
    previewEngineId: 'yolo-seg',
    // ~144 MB of weights and seconds per run; the dialog shows a Run button
    // rather than firing on every param change.
    requiresManualRun: true,
    pipelineParams: [
      {
        id: 'weightsPath', label: 'Fine-tuned Model', type: 'select',
        defaultValue: YoloModels.YOLOV8_OPTIC_NERVE.name,
        tooltip: 'Select fine-tuned model on the Optic Nerve area or on the Retina area of an eyeball section.',
        options: Object.values(YoloModels).map(m => ({ label: m.name, value: m.name })),
      },
      { id: 'saveSegment', label: 'Segmentation mode', type: 'boolean', defaultValue: false,
        tooltip: 'If enabled, runs in segmentation mode. If disabled (default), runs in detection mode.' },
      { id: 'downsamplingFactor', label: 'Downsampling Factor', type: 'number', defaultValue: 5, min: 1, max: 10, step: 1,
        tooltip: 'Downsampling value used to reduce the image size before the patching process.' },
      { id: 'overlapX', label: 'Overlap X', type: 'number', defaultValue: 0, min: 0, max: 90, step: 1,
        tooltip: 'X axis overlap value when creating patches.' },
      { id: 'overlapY', label: 'Overlap Y', type: 'number', defaultValue: 0, min: 0, max: 90, step: 1,
        tooltip: 'Y axis overlap value when creating patches.' },
      { id: 'confidence', label: 'Confidence', type: 'number', defaultValue: 0.6, min: 0.0, max: 0.9, step: 0.1,
        tooltip: 'Minimum confidence threshold value for prediction results.' },
      { id: 'iouThreshold', label: 'IoU Threshold', type: 'number', defaultValue: 0.5, min: 0.1, max: 0.9, step: 0.1,
        tooltip: 'IoU threshold for non-maximum suppression YOLOv8 of single crop.' },
      { id: 'nmsThreshold', label: 'NMS Threshold', type: 'number', defaultValue: 0.3, min: 0.1, max: 0.9, step: 0.1,
        tooltip: 'IoU/IoS threshold for non-maximum suppression. Lower values = fewer objects remain after suppression.' },
      { id: 'visualize', label: 'Visualize results', type: 'boolean', defaultValue: true,
        tooltip: 'Generate a visualization overlay image with bounding boxes and masks.' },
      // ── Browser-preview-only params. buildRequestBody ignores them, so they
      //    never reach the server; they tune the local WebGPU run.
      { id: 'serverParityClasses', label: 'Match server classes (0–4)', type: 'boolean', defaultValue: false,
        tooltip: 'Browser preview only. The server hardcodes classes 0–4, which drops Debris and Edge on the embryo models. Enable to reproduce that for a like-for-like comparison; leave off to see every class the model declares.' },
      { id: 'minArea', label: 'Min Object Area (px²)', type: 'number', defaultValue: 0, min: 0, max: 10000, step: 10,
        tooltip: 'Browser preview only. Discards detected outlines smaller than this, in square pixels.' },
      { id: 'simplifyTolerance', label: 'Outline Simplification (px)', type: 'number', defaultValue: 1, min: 0, max: 10, step: 0.5,
        tooltip: 'Browser preview only. Douglas-Peucker tolerance for the traced outlines — higher gives fewer vertices. 0 keeps the exact pixel boundary.' },
    ],
    buildRequestBody: (params, _ctx) => ({
      weightsPath: params.weightsPath || YoloModels.YOLOV8_OPTIC_NERVE.name,
      saveSegment: params.saveSegment || false,
      downsamplingFactor: params.downsamplingFactor || 5,
      overlapX: params.overlapX || 0,
      overlapY: params.overlapY || 0,
      confidence: params.confidence || 0.6,
      iouThreshold: params.iouThreshold || 0.5,
      nmsThreshold: params.nmsThreshold || 0.3,
      visualize: params.visualize !== false,
    }),
    onParamChange: (changedParamId, params) => {
      if (changedParamId !== 'weightsPath') return null;
      const model = Object.values(YoloModels).find(m => m.name === params.weightsPath);
      if (!model) return null;
      return {
        ...params,
        downsamplingFactor: model.downsamplingFactor,
        overlapX: model.overlapX,
        overlapY: model.overlapY,
        confidence: model.confidence,
        iouThreshold: model.iouThreshold,
        nmsThreshold: model.nmsThreshold,
      };
    },
  },
  {
    operationId: 'cellpose',
    processType: ProcessType.CELLPOSE,
    displayName: 'Cellpose',
    description: 'Segments cells and nuclei in fluorescence images using the Cellpose deep-learning model. Supports grayscale, two-channel, and three-channel inputs with a range of built-in and custom models.',
    icon: 'pi-microchip-ai',
    groupId: 'segmentation-classifier',
    pipelineCategory: 'segmentation',
    javaInstructionTypeName: 'org.jax.cimg.api.dao.instr.CellPoseInstruction',
    pipelineParams: [
      {
        id: 'model', label: 'Model', type: 'select', defaultValue: CellPoseModel.CYTO3,
        tooltip: 'Pre-trained U-Net model used by the v3 Cellpose worker. ' +
          'For the v4 Cellpose-SAM model, use the dedicated Cellpose-SAM tool.',
        options: Object.values(CellPoseModel)
          .filter(m => m !== CellPoseModel.CUSTOM)
          .map(m => ({ label: CellPoseModelLabels[m], value: m })),
      },
      {
        id: 'channelMode', label: 'Channel Mode', type: 'select', defaultValue: CellPoseChannelMode.GRAYSCALE,
        tooltip: 'How input channels are interpreted.',
        options: [
          { label: 'Grayscale', value: CellPoseChannelMode.GRAYSCALE },
          { label: 'Two Channel', value: CellPoseChannelMode.TWO_CHANNEL },
          { label: 'Three Channel', value: CellPoseChannelMode.THREE_CHANNEL },
        ],
      },
      { id: 'diameter', label: 'Diameter', type: 'number', defaultValue: 0.0, min: 0, max: 500, step: 1,
        tooltip: 'Expected cell diameter in pixels (0 = auto-estimate).' },
      { id: 'flowThreshold', label: 'Flow Threshold', type: 'number', defaultValue: 0.4, min: 0.0, max: 3.0, step: 0.1,
        tooltip: 'Threshold for flow error — increase to get more masks.' },
      { id: 'cellprobThreshold', label: 'Cell Prob Threshold', type: 'number', defaultValue: 0.0, min: -6.0, max: 6.0, step: 0.5,
        tooltip: 'Threshold for cell probability — decrease to get more masks.' },
      { id: 'minSize', label: 'Min Size', type: 'number', defaultValue: 15, min: 0, max: 1000, step: 1,
        tooltip: 'Minimum number of pixels per mask. Smaller masks are removed.' },
      { id: 'normalize', label: 'Normalize', type: 'boolean', defaultValue: true,
        tooltip: 'Normalize image intensity before inference.' },
      { id: 'invert', label: 'Invert', type: 'boolean', defaultValue: false,
        tooltip: 'Invert image before inference (for bright-on-dark images).' },
    ],
    buildRequestBody: (params, _ctx) => ({
      model: params.model || CellPoseModel.CYTO3,
      channelMode: params.channelMode || CellPoseChannelMode.GRAYSCALE,
      // Per-channel selection (1-based: 0 = grayscale / none, 1 = R, 2 = G, 3 = B).
      // Defaults mirror CellPoseRequest's Java-side defaults (cyto=1, nuc=2)
      // so a process-component submission without explicit values still works.
      cytoChannel: params.cytoChannel ?? 1,
      nucChannel:  params.nucChannel  ?? 2,
      diameter: params.diameter || 0.0,
      flowThreshold: params.flowThreshold ?? 0.4,
      cellprobThreshold: params.cellprobThreshold ?? 0.0,
      minSize: params.minSize || 15,
      normalize: params.normalize !== false,
      invert: params.invert || false,
      resample: true,
      batchSize: 8,
      tileOverlap: 0.1,
    }),
  },
  {
    operationId: 'cellpose-sam',
    processType: ProcessType.CELLPOSE_SAM,
    displayName: 'Cellpose-SAM',
    description:
      'Generalist cell and nuclei segmentation with the Cellpose-SAM (v4) transformer model. ' +
      'In the pipeline this is a regular JIT server operation; the unique part is that local preview ' +
      'runs Cellpose-SAM in the browser via cellpose-js (WebGPU). Run-on-Server routes through ' +
      'ProcessType.CELLPOSE_SAM (jit-cellpose-sam-worker on CPSAM_QUEUE).',
    icon: 'pi-microchip-ai',
    groupId: 'segmentation-classifier',
    // Hidden from the standalone process dropdown — the unified Cellpose process
    // page hosts the CPSAM model via its merged model dropdown. Still surfaced
    // in the pipeline operation picker via `pipelineCategory`.
    hiddenFromProcessDropdown: true,
    pipelineCategory: 'segmentation',
    // Local in-browser preview is delegated to the `cellpose` engine (cellpose-js
    // + WebGPU). The pipeline executor sees this flag and calls that engine's
    // `execute()` instead of passing-through (the default for server-engine steps).
    previewEngineId: 'cellpose',
    // CPSAM inference is ~3 s per tile; auto-rerunning on every param keystroke
    // would lock up the UI and create overlapping engine calls (the cellpose
    // engine intentionally doesn't auto-abort previous runs, which then causes
    // cancel errors). The dialog shows an explicit "Run" button instead.
    requiresManualRun: true,
    javaInstructionTypeName: 'org.jax.cimg.api.dao.instr.CellPoseSamInstruction',
    pipelineParams: [
      {
        id: 'model', label: 'Model', type: 'select', defaultValue: CellPoseSamModel.CPSAM,
        tooltip:
          'Cellpose-SAM checkpoint. Use the standalone Cellpose-SAM process page to supply a custom finetune via modelLocation.',
        options: Object.values(CellPoseSamModel)
          .filter(m => m !== CellPoseSamModel.CUSTOM)
          .map(m => ({ label: CellPoseSamModelLabels[m], value: m })),
      },
      {
        id: 'channelMode', label: 'Channel Mode', type: 'select',
        defaultValue: CellPoseChannelMode.GRAYSCALE,
        tooltip: 'How input channels are interpreted.',
        options: [
          { label: 'Grayscale',     value: CellPoseChannelMode.GRAYSCALE },
          { label: 'Two Channel',   value: CellPoseChannelMode.TWO_CHANNEL },
          { label: 'Three Channel', value: CellPoseChannelMode.THREE_CHANNEL },
        ],
      },
      { id: 'cytoChannel', label: 'Cyto Channel', type: 'select', defaultValue: 1,
        tooltip: '1-based cytoplasm channel index. Only used when Channel Mode != GRAYSCALE.',
        options: [
          { label: 'red (1)',   value: 1 },
          { label: 'green (2)', value: 2 },
          { label: 'blue (3)',  value: 3 },
        ],
      },
      { id: 'nucChannel', label: 'Nuclear Channel', type: 'select', defaultValue: 2,
        tooltip: '1-based nucleus channel index. 0 = no nucleus channel. ' +
          'Only used when Channel Mode != GRAYSCALE.',
        options: [
          { label: 'none (0)',  value: 0 },
          { label: 'red (1)',   value: 1 },
          { label: 'green (2)', value: 2 },
          { label: 'blue (3)',  value: 3 },
        ],
      },
      { id: 'diameter', label: 'Cell diameter (px)', type: 'number', defaultValue: 0, min: 0, max: 500, step: 1,
        tooltip: 'Estimated cell diameter in source pixels. 0 = native resolution (CPSAM training median ≈ 30 px).' },
      { id: 'cellprobThreshold', label: 'Cellprob threshold', type: 'number', defaultValue: 0.0,
        min: -6.0, max: 6.0, step: 0.1,
        tooltip: 'Pixels with cellprob above this enter the dynamics. Raise to be more conservative ' +
          '(typical CPSAM cellprob values are high, so small positive raises have little effect — try ' +
          'negative values to include weakly-predicted pixels).' },
    ],
    // Hide cyto/nuc channel selectors when channelMode === GRAYSCALE — they're ignored
    // by the engine in that mode and only clutter the form.
    getVisibleParams: (currentParams) => {
      const visible = new Set(['model', 'channelMode', 'diameter', 'cellprobThreshold']);
      if (currentParams.channelMode !== CellPoseChannelMode.GRAYSCALE) {
        visible.add('cytoChannel');
        visible.add('nucChannel');
      }
      return visible;
    },
    // Mirrors the Java CellPoseSamRequest — trimmed surface, no v3 flow/min-size/normalize knobs.
    buildRequestBody: (params, _ctx) => ({
      model: params.model || CellPoseSamModel.CPSAM,
      channelMode: params.channelMode || CellPoseChannelMode.GRAYSCALE,
      cytoChannel: params.cytoChannel ?? 1,
      nucChannel:  params.nucChannel  ?? 2,
      diameter: params.diameter || 0.0,
      cellprobThreshold: params.cellprobThreshold ?? 0.0,
    }),
  },

  // ── Color Processing ────────────────────────────────────────────────────────

  {
    operationId: 'deconvolution',
    processType: ProcessType.DECONVOLUTION,
    displayName: 'Color Deconvolution',
    description: 'Separates an image into its constituent color (stain) components. The default setting is for the H&E stain.',
    icon: 'pi-cog',
    groupId: 'color-processing',
    pipelineCategory: 'transform',
    javaInstructionTypeName: 'org.jax.cimg.api.dao.instr.DeconvolutionInstruction',
    pipelineParams: [
      // Paired: stain1 / stain2 (adjacent for 2-column grid flow)
      {
        id: 'stain1', label: 'Stain 1', type: 'select', defaultValue: 'hematoxylin',
        tooltip: 'First stain component for deconvolution.',
        options: STAIN_ABSORPTIONS.map(s => ({ label: s.name, value: s.name })),
      },
      {
        id: 'stain2', label: 'Stain 2', type: 'select', defaultValue: 'eosin',
        tooltip: 'Second stain component for deconvolution.',
        options: STAIN_ABSORPTIONS.map(s => ({ label: s.name, value: s.name })),
      },
      // Paired: RGB absorption coefficients
      { id: 'red1', label: 'Red 1', type: 'number', defaultValue: 0.563, min: 0, max: 1, step: 0.001,
        tooltip: 'Red absorption coefficient for stain 1.' },
      { id: 'red2', label: 'Red 2', type: 'number', defaultValue: 0.216, min: 0, max: 1, step: 0.001,
        tooltip: 'Red absorption coefficient for stain 2.' },
      { id: 'green1', label: 'Green 1', type: 'number', defaultValue: 0.720, min: 0, max: 1, step: 0.001,
        tooltip: 'Green absorption coefficient for stain 1.' },
      { id: 'green2', label: 'Green 2', type: 'number', defaultValue: 0.801, min: 0, max: 1, step: 0.001,
        tooltip: 'Green absorption coefficient for stain 2.' },
      { id: 'blue1', label: 'Blue 1', type: 'number', defaultValue: 0.406, min: 0, max: 1, step: 0.001,
        tooltip: 'Blue absorption coefficient for stain 1.' },
      { id: 'blue2', label: 'Blue 2', type: 'number', defaultValue: 0.558, min: 0, max: 1, step: 0.001,
        tooltip: 'Blue absorption coefficient for stain 2.' },
      // Paired: max OD / alpha-beta
      { id: 'stain1max', label: 'Stain 1 Max', type: 'number', defaultValue: 2.0, min: 0, max: 10, step: 0.1,
        tooltip: 'Maximum optical density for stain 1.' },
      { id: 'stain2max', label: 'Stain 2 Max', type: 'number', defaultValue: 1.0, min: 0, max: 10, step: 0.1,
        tooltip: 'Maximum optical density for stain 2.' },
      { id: 'alpha', label: 'Alpha', type: 'number', defaultValue: 1, min: 0, max: 10, step: 0.1,
        tooltip: 'Alpha coefficient for deconvolution.' },
      { id: 'beta', label: 'Beta', type: 'number', defaultValue: 0.15, min: 0, max: 1, step: 0.01,
        tooltip: 'Beta coefficient for deconvolution.' },
      // Full-width
      { id: 'intensityNorm', label: 'Intensity Norm', type: 'number', defaultValue: 240, min: 1, max: 255, step: 1,
        tooltip: 'Intensity normalization value.' },
      { id: 'grayscale', label: 'Grayscale output', type: 'boolean', defaultValue: false,
        tooltip: 'Output grayscale images instead of colour.' },
    ],
    buildRequestBody: (params, _ctx) => ({
      stain1: [params.red1 ?? 0.563, params.green1 ?? 0.720, params.blue1 ?? 0.406],
      stain2: [params.red2 ?? 0.216, params.green2 ?? 0.801, params.blue2 ?? 0.558],
      stain1Max: params.stain1max ?? 2.0,
      stain2Max: params.stain2max ?? 1.0,
      alpha: params.alpha ?? 1,
      beta: params.beta ?? 0.15,
      intensityNorm: params.intensityNorm ?? 240,
      grayscale: params.grayscale || false,
    }),
    onParamChange: (changedParamId, params) => {
      if (changedParamId === 'stain1') {
        const stain = STAIN_ABSORPTIONS.find(s => s.name === params.stain1);
        if (!stain) return null;
        return { ...params, red1: stain.value[0], green1: stain.value[1], blue1: stain.value[2] };
      }
      if (changedParamId === 'stain2') {
        const stain = STAIN_ABSORPTIONS.find(s => s.name === params.stain2);
        if (!stain) return null;
        return { ...params, red2: stain.value[0], green2: stain.value[1], blue2: stain.value[2] };
      }
      return null;
    },
  },
  {
    operationId: 'stain-adapter',
    processType: ProcessType.STAIN_ADAPTER,
    displayName: 'Stain Adaptation',
    description: 'Adapts images from a source stain to a target (reference) stain, while preserving underlying tissue morphology and biological signal. Methods used: GAN, Reinhard, Machenko, Vahadane.',
    icon: 'pi-microchip-ai',
    groupId: 'color-processing',
    pipelineCategory: 'transform',
    javaInstructionTypeName: 'org.jax.cimg.api.dao.instr.StainAdapterInstruction',
    // Browser-side preview runs on WebGPU via jax-ai-js, with normalization via
    // stain-normalization-js; Run-on-Server is unaffected and still submits a
    // StainAdapterInstruction. Five of the eight GAN checkpoints are published
    // for the browser; all four normalization methods and all three targets
    // work, including normalize-only — see StainAdapterEngine.
    previewEngineId: 'stain-adapter',
    // ~109 MB per checkpoint, plus the normalization target bundle.
    requiresManualRun: true,
    pipelineParams: [
      { id: 'normOnly', label: 'Normalize only', type: 'boolean', defaultValue: false,
        tooltip: 'If enabled, only color normalization is applied (no GAN transfer). ' +
          'Needs a normalization method to be selected.' },
      {
        id: 'normMethod', label: 'Normalization Method', type: 'select',
        defaultValue: StainAdapterNormalizeMethod.NONE,
        tooltip: 'Method used to normalize the input image. Macenko and Vahadane separate the ' +
          'image into stain concentrations and rebuild it with the target\'s stains; Reinhard and ' +
          'l_histogram shift colour statistics only. All four run in the browser preview.',
        options: Object.values(StainAdapterNormalizeMethod).map(m => ({ label: m, value: m })),
      },
      {
        id: 'normTargetPath', label: 'Normalization Target', type: 'select',
        defaultValue: StainAdapterNormalizeTarget.SMALL_RES_VAHA_MAC,
        tooltip: 'Reference image whose staining the result should adopt. The browser preview ' +
          'uses fitted state precomputed from each target at full resolution.',
        options: Object.values(StainAdapterNormalizeTarget).map(t => ({ label: t, value: t })),
      },
      { id: 'saveNormalization', label: 'Save normalization', type: 'boolean', defaultValue: true,
        tooltip: 'Save the normalized image alongside the transfer result. Server-side only — ' +
          'the browser preview shows the result rather than writing files.' },
      {
        id: 'ganModelPath', label: 'GAN Model', type: 'select',
        defaultValue: StainAdapterModel.GR2RGB_1GRAY_2ZEROS_E23_BATCHNORM_BS1_X20,
        tooltip: 'Choose GAN model to run the stain adapter process.',
        options: Object.values(StainAdapterModel).map(m => ({ label: m, value: m })),
      },
      {
        id: 'mode', label: 'Mode', type: 'select', defaultValue: StainAdapterMode.LAB,
        tooltip: 'lab: pix2pix model trained on L channel to predict AB channels. gr2rgb: pix2pix model trained on greyscale images to predict RGB images.',
        options: Object.values(StainAdapterMode).map(m => ({ label: m, value: m })),
      },
      {
        id: 'channelMode', label: 'Channel Mode', type: 'select',
        defaultValue: StainAdapterChannelMode.DEFAULT,
        tooltip: 'Channel mode the model was trained with.',
        options: Object.values(StainAdapterChannelMode).map(m => ({ label: m, value: m })),
      },
      {
        id: 'patchSize', label: 'Image Size', type: 'select', defaultValue: 512,
        tooltip: 'Image size the model was trained on.',
        options: [
          { label: '512', value: 512 },
          { label: '256', value: 256 },
        ],
      },
    ],
    buildRequestBody: (params, _ctx) => ({
      normOnly: params.normOnly || false,
      normMethod: params.normMethod,
      normTargetPath: '/app/normalization-targets/' + (params.normTargetPath || StainAdapterNormalizeTarget.SMALL_RES_VAHA_MAC),
      saveNormalization: params.saveNormalization !== false,
      model: '/app/models/' + (params.ganModelPath || StainAdapterModel.GR2RGB_1GRAY_2ZEROS_E23_BATCHNORM_BS1_X20),
      mode: params.mode || StainAdapterMode.LAB,
      channelMode: params.channelMode || StainAdapterChannelMode.DEFAULT,
      patchSize: params.patchSize || 512,
    }),
    onParamChange: (changedParamId, params) => {
      if (changedParamId !== 'ganModelPath') return null;
      const model = params.ganModelPath as string;
      if (!model) return null;

      let mode = StainAdapterMode.LAB;
      if (model.includes('gr2rgb')) mode = StainAdapterMode.GR2RGB;

      let channelMode = StainAdapterChannelMode.DEFAULT;
      if (model.includes('1gray-2zeros')) channelMode = StainAdapterChannelMode.GR_2EMPTY;
      else if (model.includes('3grays')) channelMode = StainAdapterChannelMode.TRIPLE_GRAY;
      else if (model.includes('2L')) channelMode = StainAdapterChannelMode.DOUBLE_L;
      else if (model.includes('l-empty')) channelMode = StainAdapterChannelMode.L_EMPTY;

      const patchSize = model.includes('x20') ? 256 : 512;

      return { ...params, mode, channelMode, patchSize };
    },
    getVisibleParams: (currentParams) => {
      const visible = new Set(['normOnly', 'normMethod']);
      if (currentParams.normMethod && currentParams.normMethod !== StainAdapterNormalizeMethod.NONE) {
        visible.add('normTargetPath');
        visible.add('saveNormalization');
      }
      if (!currentParams.normOnly) {
        visible.add('ganModelPath');
        visible.add('mode');
        visible.add('channelMode');
        visible.add('patchSize');
      }
      return visible;
    },
  },

  // ── Image Manipulation ──────────────────────────────────────────────────────

  {
    operationId: 'alignment',
    processType: ProcessType.ALIGNMENT,
    serverProcessType: ProcessType.SIFT,
    displayName: 'Image Alignment',
    description: 'Uses scale-invariant feature transform to align the SHG or fluorescence image with its corresponding bright-field image.',
    icon: 'pi-cog',
    groupId: 'image-manipulation',
    pipelineCategory: 'transform',
    javaInstructionTypeName: 'org.jax.cimg.api.dao.instr.SiftInstruction',
    pipelineParams: [
      { id: 'downsample', label: 'Downsample', type: 'number', defaultValue: 4, min: 1, max: 32, step: 1,
        tooltip: 'Factor to downsample images before SIFT matching (higher = faster, less precise).' },
      { id: 'flip', label: 'Flip image', type: 'boolean', defaultValue: false,
        tooltip: 'Flip the image before alignment.' },
      { id: 'gamma', label: 'Gamma correction', type: 'number', defaultValue: 1.0, min: 0.1, max: 5.0, step: 0.1,
        tooltip: 'Gamma correction applied before matching (1.0 = no change).' },
    ],
    buildRequestBody: (params, _ctx) => ({
      align: true,
      downsample: params.downsample || 4,
      flip: params.flip || false,
      gamma: params.gamma || 1.0,
    }),
  },
  {
    operationId: 'cvai',
    processType: ProcessType.CVAI,
    displayName: 'Image Processing (CV/AI)',
    description: 'Build a custom image-processing pipeline from OpenCV and AI Transformers operations and run it server-side on the full-size image.',
    icon: 'pi-sliders-h',
    groupId: 'image-manipulation',
  },
  {
    operationId: 'edof',
    processType: ProcessType.EDOF,
    displayName: 'Extended Depth of Field',
    description: 'Combines information from multiple focal planes (stack) to produce a single sharp image.',
    icon: 'pi-cog',
    groupId: 'image-manipulation',
    pipelineCategory: 'enhancement',
    javaInstructionTypeName: 'org.jax.cimg.api.dao.instr.EDoFInstruction',
    pipelineParams: [
      { id: 'gradientKernel', label: 'Gradient Kernel', type: 'select', defaultValue: 3,
        tooltip: 'Size of kernel used to measure focus — use larger values for noisier input.',
        options: [
          { label: '3x3', value: 3 },
          { label: '5x5', value: 5 },
          { label: '7x7', value: 7 },
          { label: '9x9', value: 9 },
          { label: '11x11', value: 11 },
        ] },
      { id: 'noiseFilter', label: 'Noise Filter', type: 'select', defaultValue: 3,
        tooltip: 'Size of noise filter applied to z-map.',
        options: [
          { label: '3x3', value: 3 },
          { label: '5x5', value: 5 },
          { label: '7x7', value: 7 },
          { label: '9x9', value: 9 },
          { label: '11x11', value: 11 },
        ] },
      { id: 'imageNoiseFilter', label: 'Image Noise Filter', type: 'number', defaultValue: 0, min: 0, max: 11, step: 1,
        tooltip: 'Noise filter applied to the output image (0 = disabled).' },
      { id: 'lowPass', label: 'Low-Pass Filter', type: 'number', defaultValue: 0, min: 0, max: 25, step: 1,
        tooltip: 'Size of z-map smoothing filter.' },
    ],
    buildRequestBody: (params, _ctx) => ({
      gradientKernel: params.gradientKernel || 3,
      noiseFilter: params.noiseFilter || 3,
      imageNoiseFilter: params.imageNoiseFilter || 0,
      lowPass: params.lowPass || 0,
    }),
  },

  // ── Utilities ───────────────────────────────────────────────────────────────

  // No pipelineCategory/pipelineParams/javaInstructionTypeName - this tool is deliberately NOT
  // offered in the pipeline builder (jit-ui#138 scopes only the standalone composer page; a
  // pipeline step needs a client-preview-capable or fully-declarative request shape neither of
  // which fits a free-form script). It still appears in the standalone process dropdown via
  // getProcessDropdownGroups(), the only thing that reads hiddenFromProcessDropdown.
  {
    operationId: 'qupath-script',
    processType: ProcessType.SCRIPT_QUPATH,
    displayName: 'QuPath Script',
    description: 'Runs a user-authored QuPath Groovy script against an image or directory of images, after a static-analysis safety review.',
    icon: 'pi-code',
    groupId: 'utilities',
  },

  // Same pipeline exclusion as qupath-script above, for the same reason (jit-ui#143).
  {
    operationId: 'imagej-macro',
    processType: ProcessType.SCRIPT_IMAGEJ,
    displayName: 'ImageJ Macro',
    description: 'Runs a user-authored ImageJ1 macro against an image or directory of images, after a static-analysis safety review.',
    icon: 'pi-code',
    groupId: 'utilities',
  },

  {
    operationId: 'convert',
    processType: ProcessType.CONVERT,
    displayName: 'Conversion',
    description: 'A full bioformats convert, including tiles and output filename. Some knowledge of bioformats convert is required.',
    icon: 'pi-cog',
    groupId: 'utilities',
    pipelineCategory: 'transform',
    javaInstructionTypeName: 'org.jax.cimg.api.dao.instr.ConvertInstruction',
    pipelineParams: [
      {
        id: 'option', label: 'Conversion', type: 'select', defaultValue: 'ometiff',
        tooltip: 'Target output format for BFConvert.',
        options: [
          { label: 'OME-TIFF', value: 'ometiff' },
          { label: 'OME-TIFF Pyramid', value: 'ometiff-pyramid' },
          { label: 'PNG', value: 'png' },
          { label: 'JPEG', value: 'jpeg' },
        ],
      },
      { id: 'compression', label: 'Compression', type: 'select', defaultValue: 'LZW',
        tooltip: 'Compression codec for BFConvert.',
        options: [
          { label: 'LZW', value: 'LZW' },
          { label: 'JPEG', value: 'JPEG' },
          { label: 'None', value: 'Uncompressed' },
        ],
      },
    ],
    buildRequestBody: (params, _ctx) => ({
      option: params.option || 'ometiff',
      compression: params.compression || 'LZW',
    }),
  },
];
